import { WarningAmberRounded } from '@mui/icons-material';
import { Box, Paper, Typography } from '@mui/material';
import type { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { ThemePalette } from 'mui.theme';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { isAllTimeLineDataEmpty } from 'utils/driverBehavior';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  locationColors,
  shiftColorPalette,
} from 'views/FleetPlanner/utils/constants';
import { type DispatchPlanGroupedByBumperNoType } from '../types/dispatch-summary.types';
import { getPlanTimeRange } from '../utils/dataGetters';
import { type PlanDataType } from 'views/FleetPlanner/CreateDispatchPlan/Steps/types/steps.types';
import { useAppSelector } from 'store/hook';
import {
  aggregateSchedulePlanByBumperNumber,
  normalizeSiteKey,
} from 'views/FleetPlanner/utils/dataFormatters';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { DumpingTimeLineChartTooltip } from 'views/FleetPlanner/CreateDispatchPlan/Steps/TimelineStep/components/DumpingTimeLineChartTooltip';
import '../../css/timeline.css';
import { mergeDispatchPlans } from '../utils/dataFormatters';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

const chartHeight = 90;

/**
 * Extracts dumping series data from planData.
 */

const generateDumpingSeries = (
  planDataGroupedByBumperNo: Record<string, PlanDataType[]>,
  planName: string
) => {
  if (
    !planDataGroupedByBumperNo ||
    typeof planDataGroupedByBumperNo !== 'object'
  ) {
    console.warn('planData missing or invalid format');
    return [];
  }

  const flattenedPlanData = Object.values(planDataGroupedByBumperNo).flat();

  if (!Array.isArray(flattenedPlanData)) {
    console.warn('planData missing or not array');
    return [];
  }

  const sortedPlanData = flattenedPlanData.sort(
    (a, b) => dayjs.utc(a.start_ts).valueOf() - dayjs.utc(b.start_ts).valueOf()
  );

  const lastLoadByTruck: Record<
    string,
    { location: string; materialDumped: number } | null
  > = {};

  const dumpingData: any[] = [];

  sortedPlanData.forEach((item) => {
    if (item.action === 'loading') {
      // store the load origin for this truck
      lastLoadByTruck[item.device] = {
        location: item.start_location,
        materialDumped: item.material_dumped,
      };
    }

    if (item.action === 'dumping') {
      const start = dayjs.utc(item.start_ts);
      const end = dayjs.utc(item.end_ts);

      if (
        start.isValid() &&
        end.isValid() &&
        start.valueOf() <= end.valueOf()
      ) {
        const originLocation = normalizeSiteKey(
          lastLoadByTruck[item.device]?.location
        ).toLowerCase();
        const materialPickedFromOriginLocation =
          lastLoadByTruck[item.device]?.materialDumped ?? 0;

        let colors = { fill: '#808080', stroke: '#A9A9A9' };
        colors = locationColors[originLocation] || colors;

        dumpingData.push({
          x: 'Dumping',
          y: [start.valueOf(), end.valueOf()],
          fillColor: colors.fill,
          strokeColor: colors.stroke,
          extraDetails: {
            bumperNumber: item.bumper_number,
            startLocation: originLocation,
            endLocation: originLocation,
            start: start.valueOf(),
            end: end.valueOf(),
            material_dumped: materialPickedFromOriginLocation,
          },
        });
      } else {
        console.log('Invalid entry', item.bumper_number, start, end);
      }
    }
  });

  return dumpingData.length ? [{ name: planName, data: dumpingData }] : [];
};

export const DumpingTimeLineChart = ({
  masterAlgoDataByBumperNo,
  selectedRange,
  date,
  currentShiftStartUtc: _currentShiftStartUtc,
  currentShiftEndUtc: _currentShiftEndUtc,
}: {
  masterAlgoDataByBumperNo: DispatchPlanGroupedByBumperNoType;
  selectedRange: string;
  date: dayjs.Dayjs | null;
  currentShiftStartUtc?: string;
  currentShiftEndUtc?: string;
}) => {
  const { t } = useTranslation();
  const { unitSystem, customerCode, customerTz } = useCustomerInfo();

  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const X_AXIS_LABEL_INTERVAL_MINUTES = 60;

  const approvedPlanData = useAppSelector(
    (state) => state.fleetPlanningReducer.dispatchPlanSummary
  );

  const mergedPlans = mergeDispatchPlans(approvedPlanData.plans);

  const { startDateWithTime, endDateWithTime } = useMemo(() => {
    if (selectedRange === 'Today') {
      const { masterRange, startDateWithTime } = getPlanTimeRange(
        masterAlgoDataByBumperNo.planData,
        mergedPlans.planData,
        customerTz,
        false
      );
      return {
        startDateWithTime,
        endDateWithTime: masterRange.endDateWithTime,
      };
    } else {
      const temp = getPlanTimeRange(
        masterAlgoDataByBumperNo.planData,
        mergedPlans.planData,
        customerTz,
        false
      );
      return temp;
    }
  }, [
    masterAlgoDataByBumperNo.planData,
    mergedPlans.planData,
    customerTz,
    selectedRange,
  ]);
  const xAxisMin = Math.min(startDateWithTime, endDateWithTime);
  const xAxisMax = Math.max(startDateWithTime, endDateWithTime);

  const { groupedPlanData: approvedPlanDataByBumperNo } = useMemo(
    () => aggregateSchedulePlanByBumperNumber(mergedPlans.planData),
    [mergedPlans.planData]
  );

  const baseChartOptions = useMemo<ApexOptions>(() => {
    return {
      chart: {
        height: 950,
        toolbar: {
          show: false,
          tools: {
            download: false,
          },
        },
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: false,
          dynamicAnimation: {
            enabled: false,
          },
        },
        selection: {
          enabled: false,
        },
        brush: {
          enabled: false,
        },
      },

      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '80%',
          borderRadius: 2,
        },
      },
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.1,
          },
        },
        active: {
          filter: {
            type: 'darken',
            value: 0.1,
          },
        },
      },
      colors: shiftColorPalette,
      xaxis: {
        type: 'datetime',
        min: xAxisMin,
        max: xAxisMax,
        tickAmount: 8,
        labels: {
          showDuplicates: false,
          hideOverlappingLabels: true,
          datetimeUTC: false,
          style: {
            colors: palette.textPrimary,
          },
          formatter(value: any, timestamp: any, opts: any) {
            const v = Number(value);
            if (!Number.isFinite(v)) return '';

            const labelIntervalMs = X_AXIS_LABEL_INTERVAL_MINUTES * 60 * 1000;
            const offset = v - xAxisMin;
            const remainder =
              ((offset % labelIntervalMs) + labelIntervalMs) % labelIntervalMs;

            // Apex ticks can be slightly off interval boundaries, so allow some tolerance.
            const toleranceMs = 2 * 60 * 1000;
            const shouldShow =
              remainder <= toleranceMs ||
              remainder >= labelIntervalMs - toleranceMs;

            if (!shouldShow) return '';

            return convertUtcToDisplayTime(
              v,
              'configured',
              customerTz,
              'HH:mm'
            );
          },
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        show: false,
      },

      stroke: {
        width: 1,
      },
      fill: {
        type: 'solid',
        opacity: 0.6,
      },
    };
  }, [enableDarkTheme, unitSystem, customerTz, xAxisMin, xAxisMax]);

  const masterChartOptions = useMemo<ApexOptions>(
    () => ({
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        id: 'masterTimeline',
      },
      yaxis: { show: false },
      grid: { ...baseChartOptions.grid, yaxis: { lines: { show: false } } },
    }),
    [baseChartOptions]
  );

  const dumpingChartOptions = useMemo<ApexOptions>(
    () => ({
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        sparkline: {
          enabled: true,
        },
        parentHeightOffset: 0,
      },
      tooltip: {
        followCursor: true,
        intersect: false,
        custom: (props) =>
          DumpingTimeLineChartTooltip({
            ...props,
            isMarginTop: false,
            preferredTimeDisplayMode: 'configured',
            timezone: customerTz,
          }),
      },
      xaxis: {
        ...baseChartOptions.xaxis,
        labels: { show: false },
        axisTicks: { show: false },
        axisBorder: { show: false },
      },
      yaxis: { show: false },

      legend: { show: false },
    }),
    [baseChartOptions]
  );

  const masterAlgoDumpingSeries = useMemo(
    () => generateDumpingSeries(masterAlgoDataByBumperNo.planData, ''),
    [masterAlgoDataByBumperNo.planData]
  );

  const approvedPlanDumpingSeries = useMemo(
    () => generateDumpingSeries(approvedPlanDataByBumperNo, ''),
    [approvedPlanDataByBumperNo]
  );

  const isBothTimeLineDataEmpty = useMemo(() => {
    const tempMasterAlgoFlatData = [
      { data: Object.values(masterAlgoDataByBumperNo.planData).flat() },
    ];
    const tempApprovedPlanData = [
      { data: Object.values(mergedPlans.planData).flat() },
    ];

    const isBothEmpty =
      isAllTimeLineDataEmpty(tempMasterAlgoFlatData) &&
      isAllTimeLineDataEmpty(tempApprovedPlanData);
    return isBothEmpty;
  }, [masterAlgoDataByBumperNo.planData, mergedPlans.planData]);

  return (
    <Paper
      sx={{
        height: '416px',
        position: 'relative',
        borderRadius: 2,
        width: '100%',
        border: '1px solid',
        borderColor: `borderColor.grey`,
        p: 2,
      }}
      elevation={0}
    >
      {isBothTimeLineDataEmpty ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            borderRadius: 2,
            gap: 2,
          }}
        >
          <WarningAmberRounded />
          <Typography
            sx={{
              color: palette.textPrimary,
              fontSize: '14px',
            }}
          >
            {t('no_timeline_data_available')}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'flex',
              gap: 16,
              padding: '0px',
            }}
          >
            <div
              key="Loading"
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                gap: '8px',
              }}
            >
              <Box display={'flex'} alignItems={'center'} gap={'2px'}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: locationColors.l0.fill,
                    border: `1px solid ${locationColors.l0.stroke}`,
                    marginRight: 2,
                  }}
                />
                <span style={{ fontSize: '12px' }}>
                  {t('loading_site_l0_2')}
                </span>
              </Box>

              <Box display={'flex'} alignItems={'center'} gap={'2px'}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: locationColors.l1.fill,
                    border: `1px solid ${locationColors.l1.stroke}`,
                    marginRight: 2,
                  }}
                />
                <span style={{ fontSize: '12px' }}>
                  {t('loading_site_l1_2')}
                </span>
              </Box>
              <Box display={'flex'} alignItems={'center'} gap={'2px'}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: locationColors.other.fill,
                    border: `1px solid ${locationColors.other.stroke}`,
                    marginRight: 2,
                  }}
                />
                <span style={{ fontSize: '12px' }}>{t('loading_other_2')}</span>
              </Box>
            </div>
          </Box>
          <Box
            style={{
              overflowX: 'auto',
            }}
          >
            <Box sx={{ width: '2500px' }}>
              <Box display={'flex'}>
                <Box width={'100px'}></Box>
                <Box width={'100%'}>
                  <ReactApexChart
                    options={masterChartOptions}
                    series={[{ data: [] }]}
                    type="rangeBar"
                    height={50}
                    key={Date.now()}
                  />
                </Box>
              </Box>
              <Box
                display={'flex'}
                alignItems={'center'}
                sx={{
                  border: `1px solid ${
                    enableDarkTheme ? '#334155' : '#E2E8F0'
                  }`,
                  p: 1,
                  backgroundColor: 'customBackground.section',
                  borderRadius: 2,
                }}
                mb={2}
              >
                <Box width={'100px'}>
                  <Typography
                    variant="subtitle1"
                    textAlign={'center'}
                    sx={{
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      color: palette.textPrimary,
                    }}
                  >
                    {t('dm_assign_actual')} <br /> Data
                  </Typography>
                </Box>
                <Box
                  width={'100%'}
                  sx={{
                    flexGrow: 1,
                    backgroundColor: enableDarkTheme
                      ? 'background.paper'
                      : '#FFFFFF',
                    borderRadius: 2,
                  }}
                >
                  <ReactApexChart
                    options={dumpingChartOptions}
                    series={masterAlgoDumpingSeries}
                    type="rangeBar"
                    height={chartHeight}
                    width={'100%'}
                    key={Date.now()}
                  />
                </Box>
              </Box>

              <Box
                display={'flex'}
                alignItems={'center'}
                sx={{
                  border: `1px solid ${
                    enableDarkTheme ? '#334155' : '#E2E8F0'
                  }`,
                  p: 1,
                  backgroundColor: 'customBackground.section',
                  borderRadius: 2,
                }}
              >
                <Box width={'100px'}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                    textAlign={'center'}
                  >
                    Approved <br /> {t('cdp_step_plan')}
                  </Typography>
                </Box>

                <Box
                  width={'100%'}
                  sx={{
                    flexGrow: 1,
                    backgroundColor: enableDarkTheme
                      ? 'background.paper'
                      : '#FFFFFF',
                    borderRadius: 2,
                  }}
                >
                  <ReactApexChart
                    options={dumpingChartOptions}
                    series={approvedPlanDumpingSeries}
                    type="rangeBar"
                    height={chartHeight}
                    width={'100%'}
                    key={Date.now()}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};
